import { GrowthBook, GrowthBookProvider as GBProvider } from "@growthbook/growthbook-react";
import { useEffect, useMemo, type ReactNode } from "react";
import { Tracker } from "../../domains/analytics/services/Tracker";
import { getGravityUserId } from "../../hooks/useAuth";

const CLIENT_KEY = import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY;
const API_HOST = import.meta.env.VITE_GROWTHBOOK_API_HOST || "https://cdn.growthbook.io";

/**
 * GrowthBook A/B Testing Provider
 * 
 * Wraps the app to enable feature flags and A/B testing via GrowthBook.
 * Experiment exposures are tracked via Tracker.
 * 
 * @see https://docs.growthbook.io/lib/react
 */
export function GrowthBookProvider({ children }: { children: ReactNode }) {
  const growthbook = useMemo(
    () =>
      new GrowthBook({
        ...(CLIENT_KEY
          ? {
              apiHost: API_HOST,
              clientKey: CLIENT_KEY,
            }
          : {}),
        enableDevMode: import.meta.env.DEV,
        trackingCallback: (experiment: { key: string }, result: { key: string }) => {
          Tracker.track("growthbook.experiment.viewed", {
            experiment_id: experiment.key,
            variant_id: result.key,
            $feature_flag: experiment.key,
            $feature_flag_response: result.key,
          });
        },
      }),
    [],
  );

  useEffect(() => {
    if (!CLIENT_KEY) {
      growthbook.setFeatures({});
      return;
    }

    growthbook.init({ streaming: true }).catch((err: unknown) => {
      console.warn("GrowthBook init failed:", err);
    });

    const userId = getGravityUserId();
    const isMobile = /mobile/i.test(navigator.userAgent);

    growthbook.setAttributes({
      id: userId || "anonymous",
      deviceType: isMobile ? "mobile" : "desktop",
      browser: navigator.userAgent,
      url: window.location.pathname,
    });

    return () => {
      growthbook.destroy();
    };
  }, [growthbook]);

  return <GBProvider growthbook={growthbook}>{children}</GBProvider>;
}
